import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, kind: "general" });
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Contact</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl text-slate-900 mt-2">
          Parlons de votre projet
        </h1>
        <p className="mt-4 text-lg text-slate-600">
          Une question, une demande d'atelier ou de partenariat ? Écrivez-nous !
        </p>
        <div className="mt-10 space-y-5">
          {[
            { icon: Mail, label: "Email", value: "info@kwarizmix.be", c: "bg-sky-100 text-sky-600" },
            { icon: Phone, label: "Téléphone", value: "0486 211526", c: "bg-amber-100 text-amber-600" },
            { icon: MapPin, label: "Adresse", value: "13 Courbevoie, 1348 Louvain-la-Neuve", c: "bg-rose-100 text-rose-600" },
          ].map((c) => (
            <div key={c.label} className="flex items-center gap-4">
              <span className={`grid place-items-center w-12 h-12 rounded-2xl ${c.c}`}>
                <c.icon className="w-6 h-6" />
              </span>
              <div>
                <p className="text-sm text-slate-400 font-semibold">{c.label}</p>
                <p className="font-bold text-slate-900">{c.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8">
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Nom *</Label>
              <Input required value={form.name} onChange={update("name")} className="rounded-xl mt-1" data-testid="contact-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input required type="email" value={form.email} onChange={update("email")} className="rounded-xl mt-1" data-testid="contact-email" />
            </div>
          </div>
          <div>
            <Label>Sujet *</Label>
            <Input required value={form.subject} onChange={update("subject")} className="rounded-xl mt-1" data-testid="contact-subject" />
          </div>
          <div>
            <Label>Message *</Label>
            <Textarea required rows={5} value={form.message} onChange={update("message")} className="rounded-xl mt-1" data-testid="contact-message" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-105 transition-[transform,background-color] disabled:opacity-60"
          >
            <Send className="w-5 h-5" /> {loading ? "Envoi..." : "Envoyer"}
          </button>
        </form>
      </div>
    </div>
  );
}
